import { Injectable } from '@angular/core';

/**
 * Service responsible for reading and clearing shared domain references.
 *
 * A shared domain arrives via the `?domain=<uuid>` query parameter when a user
 * opens a link that was shared with them. The UUID is read and cached eagerly
 * on construction so that multiple consumers always receive a consistent value,
 * regardless of when they call `getSharedDomainUuid()`.
 */
@Injectable({
  providedIn: 'root',
})
export class SharedDomainService {
  private _sharedDomainUuid: string | null;

  constructor() {
    const urlParams = new URLSearchParams(window.location.search);
    const domainFromUrl = urlParams.get('domain');

    this._sharedDomainUuid = domainFromUrl
      ? decodeURIComponent(domainFromUrl)
      : localStorage.getItem('sharedDomainUuid');
  }

  /**
   * Returns the cached shared domain UUID (read from URL or localStorage
   * at service construction time). Returns `null` after `clearSharedDomain()`
   * has been called.
   */
  getSharedDomainUuid(): string | null {
    return this._sharedDomainUuid;
  }

  /**
   * Removes every trace of the shared domain: resets the in-memory cache,
   * cleans `?domain=` from the URL, and removes `sharedDomainUuid` from
   * localStorage. Should be called **after** the initial domain has been
   * selected, not during selection.
   */
  clearSharedDomain(): void {
    this._sharedDomainUuid = null;
    localStorage.removeItem('sharedDomainUuid');

    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has('domain')) {
      urlParams.delete('domain');
      const newSearch = urlParams.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');

      window.history.replaceState(null, '', newUrl);
    }
  }

  /**
   * Sets the `rejectedDomainSwitch` flag in localStorage.
   * React's dashboard page reads this to skip certain post-navigation
   * refreshes when the shared domain was not accessible.
   */
  markDomainSwitchRejected(): void {
    localStorage.setItem('rejectedDomainSwitch', 'true');
  }
}
